/** Pure isometric tile geometry helpers shared by the terrain and fog renderers. */
import { gridToWorld, TILE_HALF_WIDTH, TILE_HALF_HEIGHT, ELEVATION_STEP, CHUNK_SIZE } from '@settlers/shared';
import type { GameMap } from '@settlers/engine';

export interface WorldBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Diamond outline (top, right, bottom, left) around a tile centre, as a flat
 * point list for Graphics.poly(). */
export const diamondPoints = (cx: number, cy: number): number[] => [
  cx,
  cy - TILE_HALF_HEIGHT,
  cx + TILE_HALF_WIDTH,
  cy,
  cx,
  cy + TILE_HALF_HEIGHT,
  cx - TILE_HALF_WIDTH,
  cy,
];

/** World-space bounding box covering every tile (incl. elevation & side faces)
 * of the given chunk. Used to size its baked texture and for culling. */
export const chunkWorldBounds = (map: GameMap, chunkX: number, chunkY: number): WorldBox => {
  const box: WorldBox = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };
  const x0 = chunkX * CHUNK_SIZE;
  const y0 = chunkY * CHUNK_SIZE;
  for (let y = y0; y < Math.min(y0 + CHUNK_SIZE, map.height); y++) {
    for (let x = x0; x < Math.min(x0 + CHUNK_SIZE, map.width); x++) {
      const elev = map.elevationAt(x, y);
      const c = gridToWorld(x, y, elev);
      box.minX = Math.min(box.minX, c.x - TILE_HALF_WIDTH);
      box.maxX = Math.max(box.maxX, c.x + TILE_HALF_WIDTH);
      box.minY = Math.min(box.minY, c.y - TILE_HALF_HEIGHT);
      // Side faces extend downward by elevation from the diamond's bottom.
      box.maxY = Math.max(box.maxY, c.y + TILE_HALF_HEIGHT + elev * ELEVATION_STEP);
    }
  }
  return box;
};

export const chunkCountX = (map: GameMap): number => Math.ceil(map.width / CHUNK_SIZE);
export const chunkCountY = (map: GameMap): number => Math.ceil(map.height / CHUNK_SIZE);
